import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { PRODUCTS } from "@/data/products";
import { useSEO } from "@/lib/seo";

export default function Products() {
  useSEO({
    title: "Industrial Roll Products",
    description:
      "Rubber rolls, polyurethane rolls, steel rolls, hard chromed rolls and induction hardened rolls — manufactured and refurbished by Vikas Rolls since 1970.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: PRODUCTS.map((p, i) => ({
        "@type": "Product",
        position: i + 1,
        name: p.name,
        description: p.short,
        url: `${window.location.origin}/products/${p.slug}`,
      })),
    },
  });

  return (
    <>
      <PageHero
        chapter="P"
        label="Product Range"
        title="Industrial Rolls, Built to the Application"
        sub="Five principal roll families — each manufactured, covered and finished in-house, and each specified around the process it will serve."
        crumbs={[{ label: "Products" }]}
      />
      <section className="bg-ink py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid gap-10 md:gap-14">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={0.05}>
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className={`group grid md:grid-cols-2 border border-line hover:border-steel/50 bg-ink-2 overflow-hidden ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="relative overflow-hidden min-h-[240px] md:min-h-[320px]">
                  <img src={p.image} alt={p.name} loading="lazy" className="img-industrial absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                  <span className="absolute top-4 left-4 font-mono-tech text-[11px] bg-ink/80 border border-line text-amber-brand px-3 py-1.5 tracking-[0.2em]">
                    {p.index}
                  </span>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h2 className="font-display font-black tracking-tight text-2xl md:text-4xl text-steel-light group-hover:text-amber-brand mb-4">
                    {p.name}
                  </h2>
                  <p className="text-steel text-base leading-relaxed mb-6">{p.short}</p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {p.industries.slice(0, 4).map((ind) => (
                      <span key={ind} className="font-mono-tech text-[10px] uppercase tracking-[0.15em] border border-line text-steel px-2.5 py-1">
                        {ind}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-amber-brand">
                    View details
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABand />
    </>
  );
}
